import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Edit, Trash2, Eye, Upload, FileText, Video, 
  Link as LinkIcon, Image, File, Search, Filter, X, Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeacherContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [contents, setContents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourse, setFilterCourse] = useState('all');
  const [showModal, setShowModal] = useState(searchParams.get('action') === 'add');
  const [editContent, setEditContent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content_type: 'document',
    course_id: '',
    subject_id: '',
    file_url: '',
    file_data: '',
    file_name: '',
    external_link: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('staffToken');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        navigate('/staff-portal');
        return;
      }

      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [contentRes, coursesRes, subjectsRes] = await Promise.all([
        fetch(`${API_URL}/api/lms/content`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/lms/teacher/courses`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/lms/teacher/subjects`, { headers }).then(r => r.json()).catch(() => [])
      ]);

      setContents(Array.isArray(contentRes) ? contentRes : []);
      setCourses(Array.isArray(coursesRes) ? coursesRes : []);
      setSubjects(Array.isArray(subjectsRes) ? subjectsRes : []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    try {
      const { readFileSmart } = await import('@/utils/imageCompressor');
      const result = await readFileSmart(file);
      setFormData(prev => ({
        ...prev,
        file_data: result.data,
        file_name: file.name,
        file_url: ''
      }));
      toast.success('File selected');
    } catch (err) {
      console.error('File read failed:', err);
      toast.error('Failed to read file');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.course_id) {
      toast.error('Please fill required fields');
      return;
    }

    setUploading(true);
    try {
      const url = editContent 
        ? `${API_URL}/api/lms/content/${editContent.id}`
        : `${API_URL}/api/lms/content`;
      
      const method = editContent ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editContent ? 'Content updated!' : 'Content created!');
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save content');
      }
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;

    try {
      const response = await fetch(`${API_URL}/api/lms/content/${contentId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        toast.success('Content deleted!');
        fetchData();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', content_type: 'document',
      course_id: '', subject_id: '', file_url: '', file_data: '', file_name: '', external_link: ''
    });
    setEditContent(null);
  };

  const openEdit = (content) => {
    setEditContent(content);
    setFormData({
      title: content.title || '',
      description: content.description || '',
      content_type: content.content_type || 'document',
      course_id: content.course_id || '',
      subject_id: content.subject_id || '',
      file_url: content.file_url || '',
      file_data: '',
      file_name: content.file_name || '',
      external_link: content.external_link || ''
    });
    setShowModal(true);
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="h-5 w-5 text-red-500" />;
      case 'document': return <FileText className="h-5 w-5 text-blue-500" />;
      case 'link': return <LinkIcon className="h-5 w-5 text-green-500" />;
      case 'image': return <Image className="h-5 w-5 text-purple-500" />;
      default: return <File className="h-5 w-5 text-gray-500" />;
    }
  };

  const filteredContents = contents.filter(c => {
    const matchSearch = c.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCourse = filterCourse === 'all' || c.course_id === filterCourse;
    return matchSearch && matchCourse;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/staff-portal/lms" className="p-2 hover:bg-indigo-600 rounded-lg">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold">Course Content</h1>
                <p className="text-indigo-200 text-sm">Manage your learning materials</p>
              </div>
            </div>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-white text-indigo-700 hover:bg-indigo-50">
              <Plus className="h-4 w-4 mr-2" />
              Add Content
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterCourse} onValueChange={setFilterCourse}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="All Courses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Courses</SelectItem>
                  {courses.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Content List */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredContents.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="p-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No content found. Click "Add Content" to get started.</p>
              </CardContent>
            </Card>
          ) : (
            filteredContents.map(content => (
              <Card key={content.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        {getContentIcon(content.content_type)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{content.title}</p>
                        <p className="text-xs text-gray-500">{content.course_name}</p>
                      </div>
                    </div>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded capitalize">
                      {content.content_type}
                    </span>
                  </div>
                  
                  {content.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{content.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between pt-3 border-t">
                    <span className="text-xs text-gray-400">{content.created_at?.split('T')[0]}</span>
                    <div className="flex gap-2">
                      {(content.file_url || content.external_link) && (
                        <a href={content.file_url || content.external_link} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm"><Eye className="h-4 w-4" /></Button>
                        </a>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openEdit(content)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(content.id)} className="text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editContent ? 'Edit Content' : 'Add New Content'}</h2>
              <button onClick={() => { setShowModal(false); resetForm(); }}>
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Content title"
                  required
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description"
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Course *</Label>
                  <Select value={formData.course_id} onValueChange={(v) => setFormData(prev => ({ ...prev, course_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                    <SelectContent>
                      {courses.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Subject</Label>
                  <Select value={formData.subject_id} onValueChange={(v) => setFormData(prev => ({ ...prev, subject_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select subject" /></SelectTrigger>
                    <SelectContent>
                      {subjects.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Content Type</Label>
                <Select value={formData.content_type} onValueChange={(v) => setFormData(prev => ({ ...prev, content_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document">Document (PDF/DOC)</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="link">External Link</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.content_type === 'link' ? (
                <div>
                  <Label>External Link</Label>
                  <Input
                    value={formData.external_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, external_link: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              ) : (
                <div>
                  <Label>Upload File</Label>
                  <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full">
                    <Upload className="h-4 w-4 mr-2" />
                    {formData.file_name || 'Choose File'}
                  </Button>
                  <p className="text-xs text-gray-500 mt-1">Max 10MB</p>
                </div>
              )}
              
              <div className="flex gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={uploading} className="flex-1">
                  {uploading ? 'Saving...' : <><Save className="h-4 w-4 mr-2" />{editContent ? 'Update' : 'Create'}</>}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
